import { fromMicros, istToday, toMicros } from "@superadmin/utils/cost";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@voicely/ui-mui/feedback/Dialog";
import { Button } from "@voicely/ui-mui/form/Button";
import { MenuItem } from "@voicely/ui-mui/form/MenuItem";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import {
	COST_PURPOSES,
	COST_SERVICES,
	COST_UNITS,
	type Pricing,
	type RateInput,
	rateInputZod,
	SECONDS_UNITS,
} from "@voicely/zod-schemas/costing.zod";
import { useEffect, useState } from "react";

/**
 * One rate version, as an operator types it.
 *
 * ## Prices are typed in currency units, not micros
 *
 * "0.0058 USD per 60 seconds" is what a vendor's pricing page says, so that is
 * what the form asks for; `toMicros` turns it into the integer the API stores,
 * exactly (`parseFloat` would make 0.0058 one micro short, forever).
 *
 * ## Null and "" are different answers
 *
 * A blank gateway, vendor or model means "any" — the rate is set at the level
 * above. A gateway of "(direct)" and a model of "(adapter default)" are real
 * values: a vendor billed directly, and the lane that pinned no model. The
 * selects name both so neither is typed by accident.
 *
 * Unsaved input lives in component state, deliberately not in the URL — a
 * half-typed rate in browser history is noise at best.
 */

const ANY = "__any__";
const DIRECT = "__empty__";

type Draft = {
	service: string;
	gateway: string;
	gatewayMode: "any" | "direct" | "named";
	vendor: string;
	model: string;
	modelMode: "any" | "default" | "named";
	purpose: string;
	unit: string;
	type: Pricing["type"];
	price: string;
	per: string;
	pulseSeconds: string;
	firstPulseSeconds: string;
	minimumSeconds: string;
	appliesTo: "call" | "segment";
	minimumPerEvent: string;
	bands: string;
	currency: string;
	validFrom: string;
	validTo: string;
	source: string;
	note: string;
};

function toDraft(rate: Partial<RateInput> | undefined): Draft {
	const pricing = rate?.pricing;

	return {
		service: rate?.service ?? "telephony",
		gateway: rate?.gateway ?? "",
		gatewayMode:
			rate?.gateway === undefined || rate.gateway === null
				? "any"
				: rate.gateway === ""
					? "direct"
					: "named",
		vendor: rate?.vendor ?? "",
		model: rate?.model ?? "",
		modelMode:
			rate?.model === undefined || rate.model === null
				? "any"
				: rate.model === ""
					? "default"
					: "named",
		purpose: rate?.purpose ?? ANY,
		unit: rate?.unit ?? "talk_seconds",
		type: pricing?.type ?? "per_unit",
		price:
			pricing?.type === "pulse"
				? fromMicros(pricing.pricePerMinuteMicros)
				: pricing && pricing.type !== "context_tiered"
					? fromMicros(pricing.priceMicros)
					: "",
		per: pricing && "per" in pricing ? String(pricing.per) : "1",
		pulseSeconds: pricing?.type === "pulse" ? String(pricing.pulseSeconds) : "60",
		firstPulseSeconds:
			pricing?.type === "pulse" && pricing.firstPulseSeconds
				? String(pricing.firstPulseSeconds)
				: "",
		minimumSeconds:
			pricing?.type === "pulse" && pricing.minimumSeconds ? String(pricing.minimumSeconds) : "",
		appliesTo: pricing?.type === "pulse" ? pricing.appliesTo : "call",
		minimumPerEvent: pricing?.type === "per_event_minimum" ? String(pricing.minimumPerEvent) : "",
		bands:
			pricing?.type === "context_tiered"
				? pricing.bands
						.map(
							(band) => `${band.upToPerRequest ?? "*"}:${fromMicros(band.priceMicros)}/${band.per}`,
						)
						.join("\n")
				: "200000:1.25/1000000\n*:2.5/1000000",
		currency: rate?.currency ?? "INR",
		validFrom: rate?.validFrom ?? istToday(),
		validTo: rate?.validTo ?? "",
		source: rate?.source ?? "",
		note: rate?.note ?? "",
	};
}

const num = (value: string) => (value.trim() === "" ? undefined : Number(value));

/** The form's text back into a pricing shape, or a sentence saying what is wrong. */
function toPricing(draft: Draft): Pricing | string {
	if (draft.type === "context_tiered") {
		const bands = draft.bands
			.split("\n")
			.map((line) => line.trim())
			.filter(Boolean)
			.map((line) => {
				const match = line.match(/^(\*|\d+(?:\.\d+)?)\s*:\s*([\d.]+)\s*\/\s*(\d+(?:\.\d+)?)$/);
				if (!match) return null;

				const priceMicros = toMicros(match[2] ?? "");
				if (priceMicros === null) return null;

				return {
					upToPerRequest: match[1] === "*" ? null : Number(match[1]),
					priceMicros,
					per: Number(match[3]),
				};
			});

		if (bands.some((band) => band === null)) {
			return "Each band is one line: `upTo:price/per`, with `*` as the last band's upTo.";
		}

		return { type: "context_tiered", bands: bands as NonNullable<(typeof bands)[number]>[] };
	}

	const price = toMicros(draft.price);

	if (price === null) return "The price must be a number with at most six decimals.";

	switch (draft.type) {
		case "per_unit":
			return { type: "per_unit", priceMicros: price, per: Number(draft.per) };
		case "flat_per_call":
			return { type: "flat_per_call", priceMicros: price };
		case "per_event_minimum":
			return {
				type: "per_event_minimum",
				priceMicros: price,
				per: Number(draft.per),
				minimumPerEvent: Number(draft.minimumPerEvent || 0),
			};
		case "pulse":
			return {
				type: "pulse",
				pricePerMinuteMicros: price,
				pulseSeconds: Number(draft.pulseSeconds),
				firstPulseSeconds: num(draft.firstPulseSeconds),
				minimumSeconds: num(draft.minimumSeconds),
				appliesTo: draft.appliesTo,
			};
	}
}

export function RateFormDialog({
	open,
	title,
	initial,
	submitLabel,
	pending,
	note,
	onClose,
	onSubmit,
}: {
	open: boolean;
	title: string;
	/** A line under the standing explanation, for what this use of the form means. */
	note?: string;
	initial?: Partial<RateInput>;
	submitLabel: string;
	pending?: boolean;
	onClose: () => void;
	onSubmit: (rate: RateInput) => void;
}) {
	const [draft, setDraft] = useState<Draft>(() => toDraft(initial));
	const [problem, setProblem] = useState<string | null>(null);

	// A dialog reopened for a different rate starts from that rate, not from
	// whatever the last one left behind.
	useEffect(() => {
		if (open) {
			setDraft(toDraft(initial));
			setProblem(null);
		}
	}, [open, initial]);

	const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
		setDraft((current) => ({ ...current, [key]: value }));

	const submit = () => {
		const pricing = toPricing(draft);

		if (typeof pricing === "string") {
			setProblem(pricing);
			return;
		}

		const parsed = rateInputZod.safeParse({
			service: draft.service,
			gateway:
				draft.gatewayMode === "any"
					? null
					: draft.gatewayMode === "direct"
						? ""
						: draft.gateway.trim(),
			vendor: draft.vendor.trim() === "" ? null : draft.vendor.trim(),
			model:
				draft.modelMode === "any" ? null : draft.modelMode === "default" ? "" : draft.model.trim(),
			purpose: draft.purpose === ANY ? null : draft.purpose,
			unit: draft.unit,
			pricing,
			currency: draft.currency.trim().toUpperCase(),
			validFrom: draft.validFrom,
			validTo: draft.validTo || null,
			source: draft.source.trim() || undefined,
			note: draft.note.trim() || undefined,
			supersedes: initial?.supersedes,
		});

		if (!parsed.success) {
			setProblem(parsed.error.issues.map((issue) => issue.message).join(" "));
			return;
		}

		setProblem(null);
		onSubmit(parsed.data);
	};

	const isSeconds = SECONDS_UNITS.includes(draft.unit as (typeof SECONDS_UNITS)[number]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						A rate applies at the most specific level set: model, then vendor, then gateway, then
						the service default. Leave a level on “any” to inherit it.
					</Typography>
					{note && <Alert severity="info">{note}</Alert>}

					<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
						<TextField
							select
							size="small"
							label="Service"
							value={draft.service}
							onChange={(e) => set("service", e.target.value)}
							sx={{ minWidth: 140 }}
						>
							{COST_SERVICES.map((service) => (
								<MenuItem key={service} value={service}>
									{service}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							size="small"
							label="Gateway"
							value={
								draft.gatewayMode === "any"
									? ANY
									: draft.gatewayMode === "direct"
										? DIRECT
										: "named"
							}
							onChange={(e) =>
								set(
									"gatewayMode",
									e.target.value === ANY ? "any" : e.target.value === DIRECT ? "direct" : "named",
								)
							}
							sx={{ minWidth: 170 }}
							helperText="Who bills us, if not the vendor"
						>
							<MenuItem value={ANY}>Any</MenuItem>
							<MenuItem value={DIRECT}>Billed directly</MenuItem>
							<MenuItem value="named">Named gateway…</MenuItem>
						</TextField>
						{draft.gatewayMode === "named" && (
							<TextField
								size="small"
								label="Gateway name"
								value={draft.gateway}
								onChange={(e) => set("gateway", e.target.value)}
								placeholder="livekit"
							/>
						)}
						<TextField
							size="small"
							label="Vendor"
							value={draft.vendor}
							onChange={(e) => set("vendor", e.target.value)}
							placeholder="blank = any"
						/>
					</Stack>

					<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
						<TextField
							select
							size="small"
							label="Model"
							value={draft.modelMode}
							onChange={(e) => set("modelMode", e.target.value as Draft["modelMode"])}
							sx={{ minWidth: 190 }}
						>
							<MenuItem value="any">Any</MenuItem>
							<MenuItem value="default">Adapter default (unpinned lane)</MenuItem>
							<MenuItem value="named">Named model…</MenuItem>
						</TextField>
						{draft.modelMode === "named" && (
							<TextField
								size="small"
								label="Model name"
								value={draft.model}
								onChange={(e) => set("model", e.target.value)}
								placeholder="nova-3-general / outbound"
							/>
						)}
						<TextField
							select
							size="small"
							label="Purpose"
							value={draft.purpose}
							onChange={(e) => set("purpose", e.target.value)}
							sx={{ minWidth: 150 }}
						>
							<MenuItem value={ANY}>Any</MenuItem>
							{COST_PURPOSES.map((purpose) => (
								<MenuItem key={purpose} value={purpose}>
									{purpose}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							size="small"
							label="Unit"
							value={draft.unit}
							onChange={(e) => set("unit", e.target.value)}
							sx={{ minWidth: 190 }}
						>
							{COST_UNITS.map((unit) => (
								<MenuItem key={unit} value={unit}>
									{unit}
								</MenuItem>
							))}
						</TextField>
					</Stack>

					<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
						<TextField
							select
							size="small"
							label="Pricing"
							value={draft.type}
							onChange={(e) => set("type", e.target.value as Pricing["type"])}
							sx={{ minWidth: 220 }}
						>
							<MenuItem value="per_unit">Per unit</MenuItem>
							<MenuItem value="pulse" disabled={!isSeconds}>
								Per minute, with pulse
							</MenuItem>
							<MenuItem value="per_event_minimum">Per unit, minimum per event</MenuItem>
							<MenuItem value="context_tiered">Tiered by request size</MenuItem>
							<MenuItem value="flat_per_call">Flat, once per call</MenuItem>
						</TextField>
						<TextField
							size="small"
							label="Currency"
							value={draft.currency}
							onChange={(e) => set("currency", e.target.value)}
							sx={{ width: 110 }}
						/>
						{draft.type !== "context_tiered" && (
							<TextField
								size="small"
								label={draft.type === "pulse" ? "Price per minute" : "Price"}
								value={draft.price}
								onChange={(e) => set("price", e.target.value)}
								placeholder="0.0058"
							/>
						)}
						{(draft.type === "per_unit" || draft.type === "per_event_minimum") && (
							<TextField
								size="small"
								label={`per … ${draft.unit}`}
								value={draft.per}
								onChange={(e) => set("per", e.target.value)}
								helperText="1, 60, 1000000…"
								sx={{ width: 150 }}
							/>
						)}
					</Stack>

					{draft.type === "pulse" && (
						<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
							<TextField
								size="small"
								label="First pulse (s)"
								value={draft.firstPulseSeconds}
								onChange={(e) => set("firstPulseSeconds", e.target.value)}
								placeholder="= pulse"
							/>
							<TextField
								size="small"
								label="Pulse (s)"
								value={draft.pulseSeconds}
								onChange={(e) => set("pulseSeconds", e.target.value)}
							/>
							<TextField
								size="small"
								label="Minimum (s)"
								value={draft.minimumSeconds}
								onChange={(e) => set("minimumSeconds", e.target.value)}
								placeholder="0"
							/>
							<TextField
								select
								size="small"
								label="Applies to"
								value={draft.appliesTo}
								onChange={(e) => set("appliesTo", e.target.value as Draft["appliesTo"])}
								sx={{ minWidth: 200 }}
							>
								<MenuItem value="call">The call's total</MenuItem>
								<MenuItem value="segment">Each utterance/event</MenuItem>
							</TextField>
						</Stack>
					)}

					{draft.type === "per_event_minimum" && (
						<TextField
							size="small"
							label={`Minimum per event (${draft.unit})`}
							value={draft.minimumPerEvent}
							onChange={(e) => set("minimumPerEvent", e.target.value)}
						/>
					)}

					{draft.type === "context_tiered" && (
						<TextField
							multiline
							minRows={3}
							size="small"
							label="Bands, one per line: upTo:price/per"
							value={draft.bands}
							onChange={(e) => set("bands", e.target.value)}
							helperText="A request up to 200k tokens at 1.25 per million, above it 2.5 per million: 200000:1.25/1000000 then *:2.5/1000000"
						/>
					)}

					<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
						<TextField
							size="small"
							type="date"
							label="Valid from (IST)"
							value={draft.validFrom}
							onChange={(e) => set("validFrom", e.target.value)}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
						<TextField
							size="small"
							type="date"
							label="Valid to (exclusive)"
							value={draft.validTo}
							onChange={(e) => set("validTo", e.target.value)}
							slotProps={{ inputLabel: { shrink: true } }}
							helperText="blank = open-ended"
						/>
					</Stack>

					<TextField
						size="small"
						label="Source"
						value={draft.source}
						onChange={(e) => set("source", e.target.value)}
						helperText="Required for a non-zero rate: a pricing page, an invoice, a quote."
					/>
					<TextField
						size="small"
						label="Note"
						value={draft.note}
						onChange={(e) => set("note", e.target.value)}
					/>

					{problem && <Alert severity="error">{problem}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button variant="contained" onClick={submit} disabled={pending}>
					{submitLabel}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
